import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { PageTitle, MediaField, TextField } from "./AdminKit";
import { api, formatErr } from "../../lib/api";

const Card = ({ title, children }) => (
  <div className="bg-white p-6"><h2 className="mb-5 text-lg font-semibold text-slate-900">{title}</h2><div className="space-y-4">{children}</div></div>
);

export const SettingsAdmin = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF({ ...data, certifications: Array.isArray(data.certifications) ? data.certifications : [], clients: Array.isArray(data.clients) ? data.clients : [] }); }, [data]);
  if (!f) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCert = (i, k, v) => set("certifications", f.certifications.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const setClient = (i, k, v) => set("clients", f.clients.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const save = async () => {
    try {
      await api.put("/admin/settings", f);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="admin-settings">
      <PageTitle title="Site settings" desc="Contact details, lead alerts, downloads and certifications.">
        <button data-testid="admin-settings-save" onClick={save} className="bg-[#0a6cff] px-5 py-2 text-xs font-semibold text-white">Save changes</button>
      </PageTitle>
      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Contact details">
          <TextField label="Phone" value={f.phone} onChange={(v) => set("phone", v)} testId="settings-phone" />
          <TextField label="WhatsApp number (with country code, digits only)" value={f.whatsapp} onChange={(v) => set("whatsapp", v)} testId="settings-whatsapp" hint="Example: 919876543210" />
          <TextField label="Email" value={f.email} onChange={(v) => set("email", v)} testId="settings-email" />
          <TextField label="Address" textarea value={f.address} onChange={(v) => set("address", v)} testId="settings-address" />
          <TextField label="Map search text" value={f.map_query} onChange={(v) => set("map_query", v)} testId="settings-map" hint="What to search on Google Maps, e.g. your factory name and city." />
          <TextField label="Google Maps link (for directions)" value={f.map_link} onChange={(v) => set("map_link", v)} testId="settings-map-link" />
          <TextField label="Working hours" value={f.hours} onChange={(v) => set("hours", v)} testId="settings-hours" />
        </Card>
        <div className="space-y-4">
          <Card title="Lead alerts">
            <TextField label="Send new RFQ emails to" value={f.alert_email} onChange={(v) => set("alert_email", v)} testId="settings-alert-email" hint="Every new inquiry is emailed here. Leave blank to turn off email alerts." />
            <p className="text-xs text-slate-500">WhatsApp alerts are sent via Twilio once your Twilio keys are added by your developer.</p>
          </Card>
          <Card title="Downloads (PDF)">
            <MediaField label="Company profile PDF" accept="application/pdf" value={f.company_profile_pdf} onChange={(v) => set("company_profile_pdf", v)} testId="settings-profile-pdf" />
            <MediaField label="Product catalogue PDF" accept="application/pdf" value={f.catalog_pdf} onChange={(v) => set("catalog_pdf", v)} testId="settings-catalog-pdf" />
          </Card>
          <Card title="Certifications">
            {f.certifications.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input value={c.title || ""} onChange={(e) => setCert(i, "title", e.target.value)} placeholder="ISO 9001:2015" className="w-2/5 border border-slate-300 px-3 py-2 text-sm" data-testid={`settings-cert-title-${i}`} />
                <input value={c.subtitle || ""} onChange={(e) => setCert(i, "subtitle", e.target.value)} placeholder="Certified by ..." className="flex-1 border border-slate-300 px-3 py-2 text-sm" data-testid={`settings-cert-sub-${i}`} />
                <button onClick={() => set("certifications", f.certifications.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-500" aria-label="Remove"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button data-testid="settings-cert-add" onClick={() => set("certifications", [...f.certifications, { title: "", subtitle: "" }])} className="text-xs font-semibold text-[#0a6cff]">+ Add certification</button>
          </Card>
          <Card title="Client logos (home page strip)">
            {f.clients.map((c, i) => (
              <div key={i} className="border-b border-slate-100 pb-4" data-testid={`settings-client-${i}`}>
                <div className="mb-2 flex gap-2">
                  <input value={c.name || ""} onChange={(e) => setClient(i, "name", e.target.value)} placeholder="Client name" className="flex-1 border border-slate-300 px-3 py-2 text-sm" data-testid={`settings-client-name-${i}`} />
                  <button onClick={() => set("clients", f.clients.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-500" aria-label="Remove"><X className="h-4 w-4" /></button>
                </div>
                <MediaField label="" value={c.logo} onChange={(v) => setClient(i, "logo", v)} testId={`settings-client-logo-${i}`} hint="Optional. Without a logo the name is shown in bold type." />
              </div>
            ))}
            <button data-testid="settings-client-add" onClick={() => set("clients", [...f.clients, { name: "", logo: "" }])} className="text-xs font-semibold text-[#0a6cff]">+ Add client</button>
          </Card>
        </div>
      </div>
    </div>
  );
};
